// focusLog.js — per-day ledger of completed focus sessions, synced across devices.
//
// Why: activity stats come from /goals/activity (tasks per day); the backend has
// no per-day focus read endpoint (sessions are stored, but can't be read by day).
// So each device keeps its own per-day counters, and the whole map rides along
// ui_prefs (GET/PUT /settings) together with the theme — i.e. it IS stored in the
// database and follows the user across devices.
//
// Shape (localStorage 'focus_day_log_v2' and ui_prefs.focusLog):
//   { [deviceId]: { 'YYYY-MM-DD': sessionsCount } }
// Each device only ever increments ITS OWN branch — merging is a plain
// replace-other-branches + max-own, so counts can never double.

const KEY = 'focus_day_log_v2';
const KEY_V1 = 'focus_day_log';
const DEV_KEY = 'focus_dev_id';
const KEEP_DAYS = 70;

const ymd = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function deviceId() {
  try {
    let id = localStorage.getItem(DEV_KEY);
    if (!id) { id = 'd' + Math.random().toString(36).slice(2, 10); localStorage.setItem(DEV_KEY, id); }
    return id;
  } catch { return 'd0'; }
}

function prune(days) {
  const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - KEEP_DAYS);
  const cut = ymd(cutoff);
  for (const k of Object.keys(days)) if (k < cut) delete days[k];
  return days;
}

// Raw map { devId: { ymd: n } } — migrates the old single-map format once.
export function readFocusRaw() {
  try {
    const m = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (m && typeof m === 'object') return m;
  } catch { /* fallthrough */ }
  // one-time migration from v1 ({ymd:n}) into this device's branch
  try {
    const v1 = JSON.parse(localStorage.getItem(KEY_V1) || 'null');
    if (v1 && typeof v1 === 'object') {
      const m = { [deviceId()]: prune({ ...v1 }) };
      localStorage.setItem(KEY, JSON.stringify(m));
      localStorage.removeItem(KEY_V1);
      return m;
    }
  } catch { /* ignore */ }
  return {};
}

function save(m) {
  try { localStorage.setItem(KEY, JSON.stringify(m)); } catch { /* ignore */ }
  try { window.dispatchEvent(new Event('focus_log_changed')); } catch { /* ignore */ }
}

// Record one completed session for today on THIS device.
export function recordFocusSession() {
  const m = readFocusRaw();
  const dev = deviceId();
  const mine = prune({ ...(m[dev] || {}) });
  const today = ymd();
  mine[today] = (mine[today] || 0) + 1;
  m[dev] = mine;
  save(m);
}

// Merge the server copy (from ui_prefs) into local storage.
// Other devices' branches: server wins (that's their source of truth reaching us).
// Own branch: per-day MAX — protects fresh local sessions from a stale echo.
export function applyRemoteFocus(remote) {
  if (!remote || typeof remote !== 'object') return;
  const local = readFocusRaw();
  const dev = deviceId();
  const merged = {};
  for (const [d, days] of Object.entries(remote)) {
    if (!days || typeof days !== 'object') continue;
    merged[d] = prune({ ...days });
  }
  const mineRemote = merged[dev] || {};
  const mineLocal = local[dev] || {};
  const mine = { ...mineRemote };
  for (const [k, v] of Object.entries(mineLocal)) mine[k] = Math.max(mine[k] || 0, v);
  merged[dev] = prune(mine);
  save(merged);
}

// Summed view for stats: { ymd: totalSessionsAcrossDevices }.
export function readFocusDays() {
  const m = readFocusRaw();
  const out = {};
  for (const days of Object.values(m)) {
    if (!days || typeof days !== 'object') continue;
    for (const [k, v] of Object.entries(days)) out[k] = (out[k] || 0) + (v || 0);
  }
  // Overlay authoritative server counts (see setServerFocusDays below): per day
  // we take the max — the ledger may briefly lead the server (offline queue),
  // the server may know sessions from other devices the ledger never saw.
  if (serverFocusDays) {
    for (const [k, v] of Object.entries(serverFocusDays)) out[k] = Math.max(out[k] || 0, v || 0);
  }
  return out;
}

// Merge focus sessions into an activity map ({ymd: taskCount}) without mutating it.
export function mergeFocusIntoActivity(activity) {
  const focus = readFocusDays();
  const out = { ...(activity || {}) };
  for (const [k, c] of Object.entries(focus)) out[k] = (out[k] || 0) + c;
  return out;
}

// ── Cross-device timer sync ─────────────────────────────────────────────────
// The running-timer state (endsAt/paused/label/goal/lastSession) rides along
// ui_prefs like the ledger above. Newer updatedAt wins; adopting a remote
// state notifies FocusTimerContext via a window event.
const TIMER_KEY = 'focus_timer_v1';

export function readTimerRaw() {
  try {
    const t = JSON.parse(localStorage.getItem(TIMER_KEY) || 'null');
    return t && typeof t === 'object' ? t : null;
  } catch { return null; }
}

export function applyRemoteTimer(remote) {
  if (!remote || typeof remote !== 'object') return;
  const local = readTimerRaw();
  const lu = (local && local.updatedAt) || 0;
  const ru = remote.updatedAt || 0;
  if (ru <= lu) return; // local is same or newer
  try {
    localStorage.setItem(TIMER_KEY, JSON.stringify(remote));
    window.dispatchEvent(new Event('focus_timer_remote'));
  } catch { /* ignore */ }
}

// ── Server-authoritative day counts ─────────────────────────────────────────
// /health/focus/by-day is the truth across devices; the local per-device ledger
// stays as the offline/optimistic layer. Views read the max of the two, so a
// session shows up instantly (ledger) and stays correct after sync (server).
let serverFocusDays = null;

export function setServerFocusDays(map) {
  const next = (map && typeof map === 'object') ? map : null;
  if (JSON.stringify(next) === JSON.stringify(serverFocusDays)) return; // no change → no event → no loops
  serverFocusDays = next;
  try { window.dispatchEvent(new Event('focus_log_changed')); } catch { /* ignore */ }
}
